/**
 * Accesso all'API WebMCP con shim/polyfill minimale.
 *
 * L'API nativa vive su `document.modelContext` (spec W3C CG; Chrome ha spostato
 * il getter da navigator a document: `navigator.modelContext` e` un alias
 * deprecato da Chrome 150, rimosso da 153 e gia` assente con l'origin trial su 152).
 * Se il browser la espone la usiamo cosi` com'e`:
 *   await document.modelContext.registerTool(tool, { signal })
 * (Promise, nessun unregisterTool: il tool si rimuove con signal.abort()).
 *
 * Solo in assenza di API nativa installiamo su navigator.modelContext uno shim
 * con lo stesso contratto (registerTool con { signal }). I tool vengono tenuti in
 * una Map e a ogni cambiamento emettiamo un CustomEvent("webmcp:toolschange")
 * cosi` che un eventuale bridge/estensione possa scoprirli.
 *
 * Tutto SSR-safe: in assenza di window/navigator le funzioni sono no-op.
 */

const isBrowser = () =>
  typeof window !== "undefined" && typeof navigator !== "undefined";

const hasRegisterTool = (mc) => !!mc && typeof mc.registerTool === "function";

/** API WebMCP nativa: document.modelContext, poi il vecchio alias su navigator. */
const getNativeModelContext = () => {
  if (typeof document !== "undefined" && hasRegisterTool(document.modelContext)) {
    return document.modelContext;
  }
  if (hasRegisterTool(navigator.modelContext) && !navigator.modelContext.__isShim) {
    return navigator.modelContext;
  }
  return null;
};

function createShim() {
  const tools = new Map();

  const emitChange = () => {
    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent("webmcp:toolschange"));
    }
  };

  return {
    __isShim: true,
    // Stesso contratto dell'API nativa: Promise e rimozione tramite options.signal.
    async registerTool(tool, { signal } = {}) {
      if (!tool || typeof tool.name !== "string" || !tool.name) {
        throw new Error("registerTool: 'name' obbligatorio");
      }
      if (typeof tool.execute !== "function") {
        throw new Error(`registerTool: '${tool.name}' richiede execute()`);
      }
      if (signal && signal.aborted) return;
      tools.set(tool.name, tool);
      emitChange();
      if (signal) {
        signal.addEventListener(
          "abort",
          () => {
            if (tools.get(tool.name) === tool) {
              tools.delete(tool.name);
              emitChange();
            }
          },
          { once: true }
        );
      }
    },
    listTools() {
      return Array.from(tools.values()).map(
        ({ name, description, inputSchema }) => ({
          name,
          description,
          inputSchema,
        })
      );
    },
    async callTool(name, args = {}) {
      const tool = tools.get(name);
      if (!tool) {
        throw new Error(`Tool sconosciuto: ${name}`);
      }
      return tool.execute(args);
    },
  };
}

/**
 * Ritorna l'API WebMCP da usare: quella nativa (document.modelContext) se
 * presente, altrimenti lo shim installato su navigator.modelContext.
 * Ritorna null in SSR.
 */
export function ensureModelContext() {
  if (!isBrowser()) return null;

  const native = getNativeModelContext();
  if (native) return native;

  if (!navigator.modelContext) {
    navigator.modelContext = createShim();
  }

  installDevHarness();

  return navigator.modelContext;
}

/**
 * Harness dev-only: espone window.__mcpTry(name, args) per invocare un tool
 * dalla console e loggare il risultato. Escluso in produzione.
 */
function installDevHarness() {
  if (!isBrowser()) return;
  if (process.env.NODE_ENV === "production") return;
  if (window.__mcpTry) return;

  window.__mcpTry = async (name, args = {}) => {
    try {
      const mc = navigator.modelContext;
      if (!mc) {
        console.warn("[webmcp] navigator.modelContext non disponibile");
        return null;
      }
      const result =
        typeof mc.callTool === "function"
          ? await mc.callTool(name, args)
          : null;
      console.log(`[webmcp] ${name} ->`, result);
      return result;
    } catch (error) {
      console.error(`[webmcp] errore su ${name}:`, error);
      return { isError: true, error: error.message };
    }
  };

  window.__mcpTools = () => {
    const mc = navigator.modelContext;
    const list = mc && mc.listTools ? mc.listTools() : [];
    console.table(
      list.map((t) => ({ name: t.name, description: t.description }))
    );
    return list;
  };
}
